/**
 * Add-to-home-screen ("install") support for the caregiver app.
 *
 * We intentionally ship **no service worker** (see web/app/CLAUDE.md — the app
 * deliberately avoids SW/persistence and the Firebase stale-bundle footgun that
 * comes with it). Chromium only fires `beforeinstallprompt` when a service
 * worker is present, so on our setup the event almost never fires and install
 * happens through the browser's own menu ("Install app" / the address-bar icon /
 * iOS Share → Add to Home Screen). The Settings panel therefore leads with
 * guided, per-platform steps. We still capture the event opportunistically: if a
 * browser ever does offer it, we can surface a real one-tap install button.
 */

/** The non-standard event Chromium fires when the app is installable. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

// Registered once at import (main.tsx imports this for its side effect), so the
// event — which fires early in page load — isn't missed before Settings mounts.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Stop Chrome's mini-infobar so install is driven from our Settings button.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit();
  });
}

/** Subscribe to changes in install availability. Returns an unsubscribe fn. */
export function onInstallAvailabilityChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** True when a one-tap browser install prompt is currently available. */
export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** Trigger the browser's native install dialog. Resolves to the user's choice,
 * or null if no prompt was available or the browser rejected the call. The event
 * is single-use — consumed here either way (a used prompt can't be re-shown). */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  const evt = deferredPrompt;
  if (!evt) return null;
  deferredPrompt = null;
  emit();
  try {
    await evt.prompt();
    const { outcome } = await evt.userChoice;
    return outcome;
  } catch {
    // A rejected prompt() (bad timing / already-consumed) mustn't surface as an
    // unhandled rejection; the guided steps remain as the fallback path.
    return null;
  }
}

/** True when the app is already running as an installed PWA (standalone), so
 * there's nothing to offer. Covers the iOS-Safari `navigator.standalone` flag
 * and the cross-browser display-mode media query. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

export type InstallPlatform = 'ios' | 'android' | 'desktop';

/** Best-effort platform bucket for choosing which guided steps to show. iOS
 * (any browser — they're all WebKit and only Safari's Share sheet can install)
 * vs Android vs everything else (treated as desktop). */
export function detectPlatform(): InstallPlatform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as desktop Safari; the touch-points check catches it.
  const isIOS =
    /iphone|ipad|ipod/i.test(ua) ||
    (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document);
  if (isIOS) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
}
